import { useMemo } from 'react';
import { DEFAULT_VIEW_CHUNK_LIMIT } from 'src/graphql/entities/custom-view/constants';
import { prepareSortAttributes } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { CustomViewSorting } from 'src/graphql/entities/custom-view/custom-view.types';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';
import { SiteAttributeAccessors, SiteAttributeType } from './constants';

// this hook abstracts the interface required for generic custom views table
export const useSiteRecords = ({
  filters: rawFilters,
  limit = DEFAULT_VIEW_CHUNK_LIMIT,
  sort,
  searchTerm,
  dynamicAttributePaths,
}: {
  filters: ConditionInput[];
  limit?: number;
  sort?: CustomViewSorting;
  searchTerm?: string;
  dynamicAttributePaths?: string[];
}) => {
  const filter = useDebouncedValue(rawFilters, 500);

  const options = useMemo(() => {
    return {
      searchTerm: searchTerm ?? '',
      filter,
      limit,
      sort: prepareSortAttributes<SiteAttributeType>(sort, SiteAttributeAccessors),
      entityName: EntityNames.Site,
      showResultsWhenEmptySearchTerm: true,
      dynamicAttributePaths,
    };
  }, [filter, limit, searchTerm, sort, dynamicAttributePaths]);

  const data = useSearch(options);

  if (!data) {
    throw new Error('Search is not configured to search for sites');
  }

  return data;
};
